'use client'

import type { ReactNode } from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react'
import { FeatureFlags, GlobalTheme } from '@carbon/react'

type WebsiteTheme = 'white' | 'g100'

type ThemeContextValue = {
  theme: WebsiteTheme
  isDark: boolean
  toggleTheme: () => void
}

const STORAGE_KEY = 'kmerhosting-website-theme'
const THEME_EVENT = 'kmerhosting-website-theme-change'
const ThemeContext = createContext<ThemeContextValue | null>(null)

function currentTheme(): WebsiteTheme {
  if (typeof window === 'undefined') return 'white'
  const saved = window.localStorage.getItem(STORAGE_KEY)
  if (saved === 'white' || saved === 'g100') return saved
  if (saved === 'g10') return 'white'
  if (saved === 'g90') return 'g100'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'g100' : 'white'
}

function serverTheme(): WebsiteTheme {
  return 'white'
}

function subscribeToTheme(callback: () => void) {
  if (typeof window === 'undefined') return () => undefined
  const media = window.matchMedia('(prefers-color-scheme: dark)')
  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) callback()
  }
  const handleThemeEvent = () => callback()
  const handleMedia = () => {
    if (!window.localStorage.getItem(STORAGE_KEY)) callback()
  }

  window.addEventListener('storage', handleStorage)
  window.addEventListener(THEME_EVENT, handleThemeEvent)
  media.addEventListener('change', handleMedia)

  return () => {
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener(THEME_EVENT, handleThemeEvent)
    media.removeEventListener('change', handleMedia)
  }
}

export function CarbonThemeProvider({ children }: { children: ReactNode }) {
  const theme = useSyncExternalStore<WebsiteTheme>(subscribeToTheme, currentTheme, serverTheme)

  useEffect(() => {
    document.documentElement.dataset.carbonTheme = theme
    document.documentElement.style.colorScheme = theme === 'g100' ? 'dark' : 'light'
  }, [theme])

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    isDark: theme === 'g100',
    toggleTheme: () => {
      const next: WebsiteTheme = theme === 'g100' ? 'white' : 'g100'
      window.localStorage.setItem(STORAGE_KEY, next)
      window.dispatchEvent(new Event(THEME_EVENT))
    },
  }), [theme])

  return (
    <ThemeContext.Provider value={value}>
      <FeatureFlags
        enableV12DynamicFloatingStyles
        enableV12TileDefaultIcons
      >
        <GlobalTheme theme={theme}>{children}</GlobalTheme>
      </FeatureFlags>
    </ThemeContext.Provider>
  )
}

export function useWebsiteTheme() {
  const value = useContext(ThemeContext)
  if (!value) throw new Error('useWebsiteTheme must be used inside CarbonThemeProvider')
  return value
}
